import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Loading from '../../components/Loading';
import { speak } from '../../components/TranslatableText';
import { deleteCard, isDue, reviewCard, saveCard, termKey, updateCardTranslation, type Card } from '../../lib/cards';
import { translate } from '../../lib/translate';
import { languageName } from '../../lib/languages';
import { burst } from '../../lib/confetti';
import { startOfDay } from '../../lib/dayBoundary';
import { schedule, whenLabel, type Grade } from '../../lib/srs';
import { useClassroom } from '../../state/Classroom';
import SnakeGame from './SnakeGame';

// ─────────────────────────────────────────────────────────────────────────────
// The student's flashcards: the words and phrases they saved from stories and
// videos, reviewed daily with two buttons.
//
//   Forgot  the card comes back later in THIS session (and is due tomorrow)
//   Good    the card moves up the ladder: tomorrow, 6 days, then further
//
// Only the FIRST answer to a card in a session is written to its schedule.
// A forgotten card that comes round again is practice, not a second grade —
// otherwise forgetting a card and then getting it right a minute later would
// read as "remembered".
// ─────────────────────────────────────────────────────────────────────────────

/** A forgotten card returns this many cards later. */
const REQUEUE_GAP = 3;

export default function Flashcards() {
  const { hydrated, cards } = useClassroom();
  const [mode, setMode] = useState<'overview' | 'review' | 'game'>('overview');
  if (!hydrated) return <Loading what="your flashcards" />;
  if (mode === 'review') return <Session onExit={() => setMode('overview')} />;
  if (mode === 'game') return <SnakeGame onExit={() => setMode('overview')} />;
  return <Overview cards={cards} onStart={() => setMode('review')} onGame={() => setMode('game')} />;
}

function Overview({ cards, onStart, onGame }: { cards: Card[]; onStart: () => void; onGame: () => void }) {
  const due = cards.filter(c => isDue(c));
  const dayStart = startOfDay().getTime();
  const reviewedToday = cards.filter(c => c.last_reviewed_at && new Date(c.last_reviewed_at).getTime() >= dayStart).length;

  return (
    <div className="narrow wide">
      <h1>Flashcards</h1>
      <p className="sub">Review the words and phrases you saved. A few minutes every day is what makes them stick.</p>
      <p className="small reward-line" style={{ marginTop: -6 }}>🪙 Every word you review earns a third of a sand dollar: 🪙 1 for every 3 words (each word once a day, when it’s due).</p>

      <div className="deck-hero panel">
        <div className="deck-num">
          <span className="n">{due.length}</span>
          <span className="k">due today</span>
        </div>
        <div className="grow">
          {cards.length === 0
            ? <p className="muted" style={{ margin: 0 }}>No cards yet. Tap a word in a story and press <strong>＋ Flashcard</strong>, or add your own on the <Link to="/vocab">Vocabulary</Link> page.</p>
            : due.length === 0
              ? <p style={{ margin: 0 }}><strong>All caught up.</strong> <span className="muted">Come back tomorrow{reviewedToday > 0 ? `; you reviewed ${reviewedToday} today.` : '.'}</span></p>
              : <p className="muted" style={{ margin: 0 }}>{cards.length} cards saved{reviewedToday > 0 ? ` · ${reviewedToday} reviewed today` : ''}</p>}
        </div>
        <div className="deck-actions">
          <button className="primary" disabled={due.length === 0} onClick={onStart}>
            {due.length === 0 ? 'Nothing due' : 'Start review'}
          </button>
          <button className="game-mode" disabled={due.length === 0} onClick={onGame}
            title={due.length === 0 ? 'Game Mode opens when you have cards due' : 'Review your due cards by playing Sea Snake'}>
            🐍 Game Mode
          </button>
        </div>
      </div>

      <p className="small muted" style={{ marginTop: 16 }}>
        Want to add, fix or delete a word? Go to <Link to="/vocab">Vocabulary →</Link>
      </p>
    </div>
  );
}

/** /vocab — every saved word and phrase: add your own, search, fix, delete. */
export function Vocabulary() {
  const { hydrated, cards, active } = useClassroom();
  const [q, setQ] = useState('');

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = needle
      ? cards.filter(c => c.term.toLowerCase().includes(needle) || c.translation.toLowerCase().includes(needle))
      : cards;
    return [...list].sort((a, b) => a.term.localeCompare(b.term, active?.language ?? 'es', { sensitivity: 'base' }));
  }, [cards, q]);

  if (!hydrated) return <Loading what="your vocabulary" />;

  return (
    <div className="narrow wide">
      <h1>Vocabulary</h1>
      <p className="sub">
        Every word and phrase you've saved: {cards.length} so far. Everything here is in your{' '}
        <Link to="/cards">flashcards</Link>.
      </p>

      <AddCard />

      {cards.length > 0 && (
        <>
          <div className="row" style={{ margin: '28px 0 10px' }}>
            <h2 style={{ margin: 0 }}>All words</h2>
            <span className="spacer" />
            <input style={{ maxWidth: 240 }} placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} />
          </div>
          <div className="list">
            {shown.map(c => <CardRow key={c.id} c={c} />)}
            {shown.length === 0 && <div className="notice small">Nothing matches “{q}”.</div>}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Type a word or phrase in the class language, get a translation (the same free
 * translator as the stories), fix it if you like, and add it to the deck.
 * The meaning box can also be filled in by hand without translating.
 */
function AddCard() {
  const { active, cards, setCards } = useClassroom();
  const source = active?.language ?? 'es';
  const target = active?.native_language ?? 'en';
  const [term, setTerm] = useState('');
  const [meaning, setMeaning] = useState('');
  const [busy, setBusy] = useState<'translate' | 'save' | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const lastTranslated = useRef('');

  const exists = term.trim() !== '' && cards.some(c => termKey(c.term) === termKey(term));

  const lookUp = async () => {
    const t = term.trim();
    if (!t || busy) return;
    setBusy('translate'); setMsg(null);
    try {
      const r = await translate(t, source, target);
      setMeaning(r.text);
      lastTranslated.current = t;
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : 'Translation failed. Type the meaning yourself.' });
    } finally { setBusy(null); }
  };

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    const t = term.trim();
    if (!t) return;
    if (!meaning.trim()) { void lookUp(); return; }
    setBusy('save'); setMsg(null);
    try {
      const card = await saveCard({
        term: t, translation: meaning, sourceLang: source, targetLang: target, classroomId: active?.id ?? null,
      });
      setCards(prev => [card, ...prev]);
      setMsg({ ok: true, text: `Added “${card.term}”. It’s in today’s flashcards.` });
      setTerm(''); setMeaning(''); lastTranslated.current = '';
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : 'Could not add the card.' });
    } finally { setBusy(null); }
  };

  return (
    <form className="panel add-card" onSubmit={add}>
      <h3>Add a card</h3>
      <div className="add-card-fields">
        <div className="field">
          <label htmlFor="new-term">{languageName(source)} word or phrase</label>
          <input id="new-term" dir="auto" value={term} maxLength={400} autoComplete="off" spellCheck={false}
            placeholder={source === 'es' ? 'la mariposa' : ''}
            onChange={e => { setTerm(e.target.value); setMsg(null); }}
            onBlur={() => { if (term.trim() && !meaning.trim() && lastTranslated.current !== term.trim()) void lookUp(); }} />
        </div>
        <div className="field">
          <label htmlFor="new-meaning">Meaning in {languageName(target)}</label>
          <div className="row" style={{ flexWrap: 'nowrap' }}>
            <input id="new-meaning" value={meaning} maxLength={1000} autoComplete="off"
              placeholder={busy === 'translate' ? 'Translating…' : ''} dir="auto"
              onChange={e => setMeaning(e.target.value)} />
            <button type="button" className="small" onClick={lookUp} disabled={!term.trim() || !!busy}
              title="Translate it for me">Translate</button>
          </div>
        </div>
      </div>
      {exists && <div className="small" style={{ color: 'var(--warn)', marginBottom: 8 }}>That’s already in your flashcards.</div>}
      {msg && <div className={`small ${msg.ok ? 'good-text' : 'tt-err'}`} style={{ marginBottom: 8 }}>{msg.text}</div>}
      <button className="primary" disabled={!term.trim() || !!busy || exists}>
        {busy === 'save' ? 'Adding…' : '＋ Add card'}
      </button>
    </form>
  );
}

function dueLabel(c: Card): string {
  if (isDue(c)) return c.reviews === 0 ? 'New' : 'Due';
  const days = Math.round((new Date(c.next_review).getTime() - startOfDay().getTime()) / 86_400_000);
  return days <= 1 ? 'Tomorrow' : `In ${days} days`;
}

function CardRow({ c }: { c: Card }) {
  const { setCards } = useClassroom();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(c.translation);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!value.trim() || value.trim() === c.translation) { setEditing(false); return; }
    setBusy(true);
    try {
      await updateCardTranslation(c.id, value);
      setCards(prev => prev.map(x => (x.id === c.id ? { ...x, translation: value.trim() } : x)));
      setEditing(false);
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete the card “${c.term}”?`)) return;
    setBusy(true);
    try {
      await deleteCard(c.id);
      setCards(prev => prev.filter(x => x.id !== c.id));
    } finally { setBusy(false); }
  };

  return (
    <div className="item card-row">
      <span className="grow">
        <span className="term">{c.term}</span>
        {editing
          ? (
            <span className="row" style={{ flexWrap: 'nowrap', marginTop: 4 }}>
              <input value={value} onChange={e => setValue(e.target.value)} maxLength={1000} autoFocus
                onKeyDown={e => { if (e.key === 'Enter') void save(); if (e.key === 'Escape') setEditing(false); }} />
              <button className="small" onClick={save} disabled={busy}>Save</button>
            </span>
          )
          : <span className="gloss">{c.translation}</span>}
      </span>
      <span className={`status ${isDue(c) ? 'started' : 'todo'}`}>{dueLabel(c)}</span>
      {!editing && <button className="small" onClick={() => setEditing(true)} title="Fix the translation">Edit</button>}
      <button className="small danger" onClick={remove} disabled={busy} aria-label={`Delete ${c.term}`}>✕</button>
    </div>
  );
}

// ── The review session ──────────────────────────────────────────────────────

type Slot = { card: Card; graded: boolean };

function Session({ onExit }: { onExit: () => void }) {
  const { cards, setCards, active } = useClassroom();
  // Fixed at the start: new cards saved mid-session wait for the next one.
  // Oldest-due first, never-reviewed cards last.
  const [queue, setQueue] = useState<Slot[]>(() =>
    cards.filter(c => isDue(c))
      .sort((a, b) => (a.reviews === 0 ? 1 : 0) - (b.reviews === 0 ? 1 : 0) || a.next_review.localeCompare(b.next_review))
      .map(card => ({ card, graded: false })));
  const total = useRef(queue.length);
  const [doneCount, setDoneCount] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tally, setTally] = useState({ good: 0, forgot: 0 });

  const slot = queue[0];
  const lang = slot?.card.source_lang ?? active?.language ?? 'es';

  const grade = useCallback(async (g: Grade) => {
    if (!slot || busy) return;
    setError(null);
    if (!slot.graded) {
      setBusy(true);
      try {
        const updated = await reviewCard(slot.card, g);
        setCards(prev => prev.map(c => (c.id === updated.id ? updated : c)));
        setTally(t => ({ ...t, [g]: t[g] + 1 }));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not save. Try again.');
        setBusy(false);
        return;
      }
      setBusy(false);
    }
    setRevealed(false);
    setQueue(q => {
      const [head, ...rest] = q;
      if (g === 'forgot') {
        const at = Math.min(REQUEUE_GAP, rest.length);
        return [...rest.slice(0, at), { card: head.card, graded: true }, ...rest.slice(at)];
      }
      return rest;
    });
    if (g === 'good') setDoneCount(n => n + 1);
  }, [slot, busy, setCards]);

  // Keyboard: space reveals, then 1 = Forgot, 2 or space = Good.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.tagName === 'INPUT') return;
      if (!revealed && (e.key === ' ' || e.key === 'Enter')) { e.preventDefault(); setRevealed(true); return; }
      if (!revealed) return;
      if (e.key === '1') void grade('forgot');
      if (e.key === '2' || e.key === ' ' || e.key === 'Enter') { e.preventDefault(); void grade('good'); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [revealed, grade]);

  const finished = !slot;
  useEffect(() => { if (finished && total.current > 0) burst(); }, [finished]);

  if (finished) {
    return (
      <div className="review-wrap">
        <div className="fc-card done-card">
          <div className="big-emoji" aria-hidden>🎉</div>
          <h2 style={{ margin: 0 }}>Done for today!</h2>
          <p className="muted" style={{ margin: 0 }}>
            {tally.good} remembered · {tally.forgot} to practise again tomorrow
          </p>
          <div className="row" style={{ justifyContent: 'center', marginTop: 8 }}>
            <button onClick={onExit}>Back to flashcards</button>
            <Link to="/" className="button">Home</Link>
          </div>
        </div>
      </div>
    );
  }

  const preview = schedule(
    { interval: slot.card.interval, easeFactor: slot.card.ease_factor, reviewCount: slot.card.review_count }, 'good',
  );

  return (
    <div className="review-wrap">
      <div className="row" style={{ marginBottom: 10 }}>
        <button className="small" onClick={onExit}>← Stop</button>
        <span className="spacer" />
        <span className="small muted">{doneCount} / {total.current}</span>
      </div>
      <div className="progress"><i style={{ width: `${(doneCount / Math.max(total.current, 1)) * 100}%` }} /></div>

      <div className={`fc-card${revealed ? ' revealed' : ''}`} onClick={() => !revealed && setRevealed(true)}>
        <div className="badge">
          {slot.graded ? 'Again' : slot.card.reviews === 0 ? 'New' : slot.card.kind === 'phrase' ? 'Phrase' : 'Word'}
        </div>
        <div className="fc-front">
          {slot.card.term}
          <button className="icon" aria-label="Listen" onClick={e => { e.stopPropagation(); speak(slot.card.term, lang); }}>🔊</button>
        </div>
        {revealed
          ? <><div className="divider" /><div className="fc-back">{slot.card.translation}</div></>
          : <div className="small muted">Think of what it means, then tap the card<span className="keys-only"> or press space</span></div>}
      </div>

      {error && <div className="notice bad small" style={{ marginTop: 12 }}>{error}</div>}

      {revealed
        ? (
          <div className="fc-grades">
            <button className="forgot" onClick={() => void grade('forgot')} disabled={busy}>
              <span className="lbl">Forgot</span>
              <span className="when">see it again soon</span>
            </button>
            <button className="good" onClick={() => void grade('good')} disabled={busy}>
              <span className="lbl">Good</span>
              <span className="when">{slot.graded ? 'next card' : whenLabel(preview.interval)}</span>
            </button>
          </div>
        )
        : (
          <button className="primary fc-show" onClick={() => setRevealed(true)}>Show answer</button>
        )}
    </div>
  );
}
